/**
 * Modal queue that enforces one modal showing at a time, but allows all modals
 * an opportunity to display.
 *
 * Modals are enqueued with a priority assigned. Modals with a higher priority
 * will display before those with a lower priority. If a modal is currently
 * visible and a higher priority modal is enqueued, the lower priority modal
 * will be hidden. Once the higher priority modal is closed, the lower priority
 * modal will re-display.
 *
 * See `useQueue` usage instructions.
 */

type Modal = {
  id: number;
  priority: number;
};

type SetCurrent = (_: number) => void;

class ModalQueue {
  private modals: Modal[] = [];

  private readonly setCurrent;

  constructor(setCurrent: SetCurrent) {
    this.setCurrent = () => {
      setCurrent(this.modals[0]?.id ?? -1);
    };
  }

  enqueue(id: number, priority: number) {
    const modal: Modal = { id, priority };
    this.modals = [...this.modals, modal].sort(
      (a, b) => b.priority - a.priority
    );
    this.setCurrent();
  }

  dequeue(id: number) {
    this.modals = this.modals.filter(m => m.id !== id);
    this.setCurrent();
  }
}

export default ModalQueue;
